import { Fragment, ReactNode } from 'react';

import styled, { CSSProperties } from 'styled-components';

import { CardPropsSpaceSplit } from '@components/card/card';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { Link } from '@components/link';
import { Stack } from '@components/stack';
import { TitleDescription, TitleDescriptionProps } from '@components/title-description/';
import { pxToRem } from '@utils/converters';
import { transitionStandard } from '@utils/transitions';
import { MakePropertiesRequired } from '@utils/types';

import { Card } from '../card';
import { Menu, MenuItem, MenuItemProps, MenuSeparator } from '../menu/menu';

type ActionItem = MakePropertiesRequired<MenuItemProps, 'id'> | 'separator';

export type ListCardProps = (CardPropsSpaceSplit | { spaceX?: never; spaceY?: never }) &
  TitleDescriptionProps & {
    actionItems?: ActionItem[];
    actions?: ReactNode;
    to?: string;
    isActive?: boolean;
    onClick?: () => void;
    styles?: CSSProperties;
    className?: string;
  };

export const ListCard = ({
  actionItems,
  actions,
  spaceX = 6,
  spaceY = 6,
  to = '',
  isActive,
  styles,
  iconRight,
  className,
  onClick,
  ...rest
}: ListCardProps) => {
  const hasLink = to.length > 0;
  const Wrapper = hasLink ? Link : Fragment;
  const wrapperProps = hasLink ? { to } : {};

  return (
    <Wrapper {...wrapperProps}>
      <StyledCard
        className={className}
        interactive={hasLink || Boolean(onClick)}
        spaceX={spaceX}
        spaceY={spaceY}
        isActive={isActive}
        onClick={onClick}
        {...rest}
        style={{
          ...styles,
          ...(onClick ? { cursor: 'pointer' } : {}),
        }}
      >
        <TitleDescription {...rest} />
        <ActionContainer orientation="horizontal" space={4}>
          {actions}
          {actionItems && (
            <Menu
              buttonProps={{
                iconLeft: <IconMoreVertical />,
                variant: 'ghost',
              }}
              placement="left-start"
            >
              {actionItems.map((item, idx) =>
                item === 'separator' ? (
                  <MenuSeparator key={idx} />
                ) : (
                  <MenuItem key={item.id} {...item} />
                ),
              )}
            </Menu>
          )}
        </ActionContainer>
        {iconRight}
      </StyledCard>
    </Wrapper>
  );
};

const ActionContainer = styled(Stack)({
  minWidth: 'fit-content',
});

const StyledCard = styled(Card)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: pxToRem(16),
  transition: transitionStandard('background', 'border-color', 'box-shadow'),
});
